import React from "react";

interface AnimatedCheckProps {
  checked: boolean;
  size?: number;
  /** White box + navy tick (welcome video variant). */
  inverted?: boolean;
}

/**
 * iOS `OnboardingAnimatedCheck`: tilted outline that springs upright while the
 * box fills and the tick draws in.
 */
export const OnboardingAnimatedCheck: React.FC<AnimatedCheckProps> = ({ checked, size = 22, inverted = false }) => {
  const radius = size * 0.23;
  const strokeBox = inverted ? (checked ? "border-white" : "border-white/45") : checked ? "border-peak-text" : "border-peak-border";
  const fillBox = inverted ? "bg-white" : "bg-peak-text";
  const tick = inverted ? "#0A1628" : "var(--peak-bg-primary)";
  return (
    <span className="relative block shrink-0" style={{ width: size, height: size }} aria-hidden="true">
      <span
        className={`absolute inset-0 border transition-all duration-300 ease-out ${strokeBox}`}
        style={{ borderRadius: radius, borderWidth: checked ? 2 : 1.5, transform: `rotate(${checked ? 0 : -7}deg)` }}
      />
      <span
        className={`absolute inset-0 transition-all duration-300 ease-out ${fillBox}`}
        style={{
          borderRadius: radius,
          opacity: checked ? 1 : 0,
          transform: `scale(${checked ? 1 : 0.4}) rotate(${checked ? 0 : -10}deg)`,
        }}
      />
      <svg
        viewBox="0 0 24 24"
        className="absolute"
        style={{ width: size * 0.62, height: size * 0.62, left: size * 0.19, top: size * 0.19 }}
        fill="none"
        stroke={tick}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path
          d="M5 13 Q7.5 13.5 10 18 Q13 11 19.5 6.5"
          pathLength={1}
          style={{
            strokeDasharray: 1,
            strokeDashoffset: checked ? 0 : 1,
            transition: "stroke-dashoffset 420ms ease-in-out",
          }}
        />
      </svg>
    </span>
  );
};

/** Tick only (no box) — "what you log daily" list. */
export const OnboardingAnimatedTick: React.FC<{ checked: boolean; size?: number }> = ({ checked, size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-peak-text" aria-hidden="true">
    <path
      d="M5 13 Q7.5 13.5 10 18 Q13 11 19.5 6.5"
      pathLength={1}
      style={{ strokeDasharray: 1, strokeDashoffset: checked ? 0 : 1, transition: "stroke-dashoffset 420ms ease-in-out" }}
    />
  </svg>
);

interface RowProps {
  title: string;
  selected: boolean;
  onClick: () => void;
  role?: "radio" | "checkbox";
}

/** 56px, radius 8, border-default, 18px h-padding, animated check + Funnel 16. */
const OnboardingOptionRow: React.FC<RowProps> = ({ title, selected, onClick, role = "radio" }) => (
  <button
    type="button"
    role={role}
    aria-checked={selected}
    onClick={onClick}
    className="flex w-full min-h-[56px] items-center gap-3.5 rounded-lg border border-peak-border bg-peak-bg px-[18px] text-left transition-colors hover:bg-peak-bg-secondary/40"
  >
    <OnboardingAnimatedCheck checked={selected} />
    <span className="font-funnel text-[16px] text-peak-text">{title}</span>
  </button>
);

export const OnboardingRadioRow: React.FC<Omit<RowProps, "role">> = (props) => <OnboardingOptionRow {...props} role="radio" />;
export const OnboardingCheckRow: React.FC<Omit<RowProps, "role">> = (props) => <OnboardingOptionRow {...props} role="checkbox" />;
